// <purpose>WIM triage panel (detail view): handling chip + urgency/reminder badges + the override
// SIGNAL badge (a fired shared override, rendered beside the classification) + routing target line
// + assisted confirm actions (Weiterleitung bestätigen → drafts; Ablegen). The panel is the
// clerk-facing rendering of the persisted triage record — it decides NOTHING, it renders the
// record and forwards a confirm.</purpose>
// <dependencies>TriageRecord from types (camelCase wire DTO) · smarttix.confirmTriageAction ·
// useCategoryMeta (the ONE label seam — both chips resolve through it, no private label map) ·
// theme tokens (no invented palette).</dependencies>
// <constraints>UI Layout Stability: both action buttons render ALWAYS (disabled with a
// title-text reason rather than absent), so confirming/eligibility changes never shift layout.
// The two vocabulary-derived chips shimmer in pre-allocated space while cm.configLoading —
// never placeholder text, never an absent chip. An unaddressed rule shows „Ziel unbestätigt" and
// a disabled forward — an address is never guessed. Internal text only, never customer-facing.
// No emojis.</constraints>
import React, { useState } from 'react';
import { TriageRecord } from '../types';
import { smarttix } from '../services/api';
import { announceTicketMutated } from '../services/ticketSync';
import { theme } from '../theme';
import { WIM_HANDLING } from '../constants';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { SkeletonBlock } from './shared';
import { SecondaryAxisPills } from './SecondaryAxisPills';
import { InfoTooltip } from './InfoTooltip';
import { overrideSignalBadge, overrideNoun, LEGACY_OVERRIDE_DECISION_SOURCE } from '../services/replyEngineMeta';

const TriagePanel: React.FC<{
  uuid: string;
  suggestionVersion: number;
  triage: TriageRecord;
  onActionDone?: (resultState: string) => void;
}> = ({ uuid, suggestionVersion, triage, onActionDone }) => {
  // Both chips resolve through the SAME seam every list row and badge reads (vocabulary → legacy
  // schema → the role-gated CATEGORY_LABELS floor). This panel used to read CATEGORY_LABELS
  // directly, which made it the one surface a tenant could not re-word from its own data: a
  // vocabulary rename would land on the row and NOT in the panel, both on screen at once.
  const cm = useCategoryMeta();
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const addressed = !!triage.routingTargetAddress;
  // Override SIGNAL (role-vocabulary round §5): since that round the shared override no longer
  // replaces the classification at the wim call site — it is recorded (overrideVerdict) and rendered
  // BESIDE the real handling/process chips, never instead of them. Standard chip colouring on
  // purpose: it is a signal, not an alarm; the urgency chip already owns red.
  const overrideSignal = triage.overrideVerdict
    ? overrideSignalBadge(triage.overrideVerdict, triage.decisionSource !== LEGACY_OVERRIDE_DECISION_SOURCE)
    : null;
  // The PERSISTED claim, not just this mount's memory: the server's single-use gate stamps
  // triage.action on the first confirm, so a re-opened (or already archived) ticket must render
  // both buttons disabled with the honest reason — before this read, `result` was mount-local
  // and a fresh mount offered Ablegen again on an archived ticket (operator repro 2026-08-31),
  // with the „Bereits bestätigt" rejection only surfacing AFTER the click.
  const claimed = triage.action ?? null;
  const CLAIM_LABELS: Record<string, string> = { forward: 'Weiterleitung', ack: 'Empfangsbestätigung', archive: 'Ablegen' };
  const claimedTooltip = claimed
    ? `Bereits bestätigt (${CLAIM_LABELS[claimed.type] ?? claimed.type})`
    : null;
  const forwardEnabled = triage.handling === WIM_HANDLING.forward && addressed && !busy && !result && !claimed;
  const archiveEnabled = !busy && !result && !claimed;
  const forwardTooltip = claimedTooltip
    ?? (triage.handling !== WIM_HANDLING.forward
      ? 'Nur für Weiterleiten-Vorschläge'
      : !addressed
        ? 'Ziel unbestätigt — keine freigegebene Zieladresse hinterlegt'
        : 'Weiterleitungs-Entwurf erzeugen');
  // A LEGACY short-circuit record (decisionSource "override", see LEGACY_OVERRIDE_DECISION_SOURCE
  // above) stamped its raw override id straight into `reason` — the classifier never ran to
  // produce prose. overrideNoun is the SAME OVERRIDE_NOUNS home the signal badge's face already
  // reads (replyEngineMeta); no second mapping. A normal LLM reason (post-split records, every
  // non-override row) passes through untouched.
  const begruendung = triage.decisionSource === LEGACY_OVERRIDE_DECISION_SOURCE && triage.reason
    ? overrideNoun(triage.reason)
    : triage.reason;

  const confirm = async (action: 'forward' | 'archive') => {
    setBusy(action);
    setError(null);
    const res = await smarttix.confirmTriageAction(uuid, suggestionVersion, action);
    setBusy(null);
    if (res.success) {
      setResult(res.resultState ?? action);
      // Drift note (mirrors ArchivSeite.handleRestore): this calls smarttix directly rather
      // than a tm.* mutator, so it misses the TicketManagerContext chokepoint's automatic
      // announce. Announce explicitly — a popout window or another tab on this uuid needs to
      // know the triage record moved.
      announceTicketMutated(uuid);
      onActionDone?.(res.resultState ?? action);
    } else {
      setError(res.error ?? res.resultState ?? 'Aktion fehlgeschlagen');
    }
  };

  const chip = (content: React.ReactNode, opts?: { strong?: boolean }) => (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 10,
      color: opts?.strong ? theme.colors.white : theme.colors.navy,
      backgroundColor: opts?.strong ? theme.colors.error : theme.colors.white,
      border: `1px solid ${opts?.strong ? theme.colors.error : theme.colors.borderLight}`,
      whiteSpace: 'nowrap',
    }}>{content}</span>
  );

  // Loading posture (the standing FE rule: shimmer in pre-allocated space, never a placeholder
  // text flash). cm.configLoading is true ONLY during a session's genuine first /config fetch —
  // and during it every label accessor would fall through to the raw `{category.…}` floor, which
  // is exactly the text this panel must never flash. Same self-guarding shape as the other
  // vocab-dependent surfaces (DistributionHero, VolumenJeKategorieCard): the guard lives IN the
  // component, so every mount inherits it rather than each call site remembering.
  // Only the two chips below are vocab-derived — the urgency/reminder/ENTWURF badges, the Ziel
  // line, the Begründung and both buttons are literal German and render for real throughout.
  // Skeleton height 13 ≈ the 11px chips' own line box, so the shimmer→text swap does not move the
  // row; widths are the median label widths of the served wim axes.
  const labelChips = cm.configLoading
    ? (
      <>
        {chip(<SkeletonBlock width={78} height={13} borderRadius={6} style={{ display: 'inline-block', verticalAlign: 'middle' }} />)}
        {triage.processType && chip(<SkeletonBlock width={62} height={13} borderRadius={6} style={{ display: 'inline-block', verticalAlign: 'middle' }} />)}
      </>
    ) : (
      <>
        {chip(cm.label(triage.handling))}
        {/* Secondary axis (process type) — the divergence this migration ends: this chip used to
            render cm.label's FULL label with no overflow handling, because it was written
            separately from every other secondary-axis surface. codes is always a singleton here
            (one triage record has one process type), so +N never fires; maxVisible={1} is the
            honest cap for that, not a guess.
            Three visual changes from the old chip(), named (fix round 1, LOW-3) so they read as
            deliberate, not accidental: fontWeight 600->500 and background white->bgLight are the
            standard secondary-axis colouring this ruling is fleet-wide about — the typographic
            register that tells a scanning clerk which axis they're looking at, correct here same
            as everywhere else. Height is unaffected (both arms share fontSize 11, padding
            '2px 10px', borderRadius 10, a 1px border), so no-layout-shift holds on that axis — but
            SecondaryAxisPills is flexShrink:0 where the loading skeleton's chip() wrapper is a
            default flex item (flexShrink:1), so the two states can WRAP differently inside this
            row's flexWrap:'wrap' at narrow widths. Not a regression on the axis the rule is about,
            but worth naming since it's a real difference between the two arms. */}
        {triage.processType && <SecondaryAxisPills codes={[triage.processType]} maxVisible={1} variant="detail" />}
      </>
    );

  return (
    <div data-testid="triage-panel" style={{
      padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
      backgroundColor: theme.colors.bgPage,
      border: `1px solid ${theme.colors.borderLight}`,
      borderRadius: theme.borderRadius.md,
      fontSize: 13, lineHeight: 1.6, color: theme.colors.navyMid,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: theme.spacing.xs, marginBottom: theme.spacing.xs }}>
        {labelChips}
        {triage.urgency === 'hoch' && chip('DRINGEND', { strong: true })}
        {triage.isReminder && chip('Erinnerung')}
        {triage.ackEligible && triage.ackWatermark && chip('ENTWURF')}
        {overrideSignal && (
          <InfoTooltip text={overrideSignal.hover}>
            <span data-testid="override-signal-badge">{chip(overrideSignal.face)}</span>
          </InfoTooltip>
        )}
      </div>

      {/* Ziel line renders ALWAYS for H1 (pre-allocated) — unaddressed = the honest label. */}
      {triage.handling === WIM_HANDLING.forward && (
        <div data-testid="triage-target">
          <span style={{ fontWeight: 600 }}>Ziel:</span>{' '}
          {addressed
            ? `${triage.routingTargetUnit ?? triage.routingTargetAddress} (${triage.routingTargetAddress})`
            : triage.routingTargetUnit
              ? `${triage.routingTargetUnit} — Ziel unbestätigt`
              : 'Ziel unbestätigt'}
        </div>
      )}
      {triage.reason && (
        <div><span style={{ fontWeight: 600 }}>Begründung:</span> {begruendung}</div>
      )}

      <div style={{ display: 'flex', gap: theme.spacing.sm, marginTop: theme.spacing.sm, minHeight: 32 }}>
        <InfoTooltip text={forwardTooltip}>
          <button
            type="button"
            disabled={!forwardEnabled}
            onClick={() => confirm('forward')}
          >
            Weiterleitung bestätigen
          </button>
        </InfoTooltip>
        <InfoTooltip text={claimedTooltip ?? 'Vorgang ablegen (archivieren)'}>
          <button
            type="button"
            disabled={!archiveEnabled}
            onClick={() => confirm('archive')}
          >
            Ablegen
          </button>
        </InfoTooltip>
        {/* Result/error slot is pre-allocated via minHeight so outcomes never shift layout. */}
        <span aria-live="polite" style={{ alignSelf: 'center', color: error ? theme.colors.error : theme.colors.navyMid }}>
          {result || claimed ? 'Bestätigt' : error ?? ''}
        </span>
      </div>
    </div>
  );
};

export default TriagePanel;
